import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TokenService } from '../../services/token.service';

/**
 * Hybrid auth bridge — attaches `Authorization: Bearer <jwt>` to NestJS
 * (apiV2) requests. See WEA-510 / nest/docs/architecture.md §8.1.
 *
 * Passthrough conditions (no header attached, no retry logic):
 *   - `environment.jwtAuthEnabled` is false (gate off)
 *   - request is not to `environment.apiV2` (legacy Contensive call)
 *   - request targets a `/public/` route — these are `@Public()` on the NestJS
 *     side (the guard never inspects the Bearer), so attaching a token is
 *     pointless. Critically, fetching one isn't free: an anonymous flow (sign-up)
 *     has no cookie session, so `fetchToken()` → `/getApiToken/` 401s and calls
 *     `markFellBack()`, poisoning the token cache for the rest of the session.
 *     (Optional-auth public endpoints — e.g. a future IsUserLoggedIn — would need
 *     "attach if cached, never fetch"; revisit this guard when one lands.)
 *
 * Token handling (WEA-638 — proactive attach with reactive fallback):
 *   - If a valid token is cached, attach it and send.
 *   - If the cache is cold (first request after reload, or after the JWT TTL
 *     lapses), `fetchToken()` FIRST and attach the result, so the request goes
 *     out with the Bearer instead of provoking a wasted 401 → refresh → retry.
 *     TokenService single-flights the refresh, so a burst of cold requests
 *     triggers one fetch.
 *   - 401 from a NestJS call (token expired mid-flight / revoked) → re-fetch
 *     (deduped via TokenService) and retry the request once. A second 401 marks
 *     TokenService as fallen-back and the original error propagates.
 *
 * Registered LAST in main.ts so it's innermost in the chain — its 401-retry can
 * swallow the response before the legacy `AuthInterceptor` sees it and redirects
 * to /login.
 *
 * @hybrid — retires with WEA-514.
 */
export const bearerInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.jwtAuthEnabled) return next(req);
  if (!req.url.startsWith(environment.apiV2)) return next(req);
  // Public NestJS endpoints need no Bearer; never trigger a token fetch for them.
  if (req.url.startsWith(`${environment.apiV2}/public/`)) return next(req);

  const tokenService = inject(TokenService);

  /** Send the request with the given token (or none), with a single 401 retry. */
  const sendWith = (token: string | null): Observable<HttpEvent<unknown>> =>
    next(token ? withBearer(req, token) : req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) return throwError(() => err);
        return tokenService.fetchToken().pipe(
          switchMap((newToken) => {
            if (!newToken) return throwError(() => err);
            return next(withBearer(req, newToken)).pipe(
              catchError((retryErr: HttpErrorResponse) => {
                if (retryErr.status === 401) tokenService.markFellBack();
                return throwError(() => retryErr);
              }),
            );
          }),
        );
      }),
    );

  const cached = tokenService.getAccessToken();
  if (cached) return sendWith(cached);

  // Cold cache: mint/refresh the token before sending so the first request
  // carries the Bearer (no wasted 401). fetchToken() returns null when the
  // bridge is off or the cookie session is invalid — then we send unauthenticated
  // and let the 401 path / AuthInterceptor handle it.
  return tokenService.fetchToken().pipe(switchMap((token) => sendWith(token)));
};

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
